"use client";

import { ArrowRight, Clock, Loader, Mail, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const contactBlocks = [{
  icon: "phone",
  label: "TELEPHONE",
  detail: "+1(475)332-6003",
  href: "tel:4753326003",
  interactive: true
}, {
  icon: "mail",
  label: "EMAIL",
  detail: "asahisearch@gmail.com",
  href: "mailto:asahisearch@gmail.com",
  interactive: true
}, {
  icon: "clock",
  label: "HOURS",
  detail: "Monday — Friday: 9:00 — 18:00",
  href: "",
  interactive: false
}];
function ConciergeForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-10 border border-primary/40 bg-primary/5 backdrop-blur-sm rounded-lg">
        <p className="text-xs uppercase tracking-[0.2em] font-default text-primary mb-4">
          02 — MESSAGE SENT
        </p>
        <p className="text-2xl font-default text-foreground mb-8 leading-snug">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="border-border text-foreground hover:bg-foreground hover:text-background uppercase tracking-[0.15em] text-xs h-12 px-6 rounded-lg">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <p className="text-[11px] uppercase tracking-[0.25em] font-default text-muted-foreground">
        INQUIRY FORM
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[11px] uppercase tracking-[0.25em] font-default text-muted-foreground">
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="bg-muted border-0 border-b border-border px-4 h-12 text-base font-default text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary rounded-lg transition-all" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[11px] uppercase tracking-[0.25em] font-default text-muted-foreground">
                Email
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="bg-muted border-0 border-b border-border px-4 h-12 text-base font-default text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary rounded-lg transition-all" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[11px] uppercase tracking-[0.25em] font-default text-muted-foreground">
              Phone Number
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 555-123-4567" {...field} className="bg-muted border-0 border-b border-border px-4 h-12 text-base font-default text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary rounded-lg transition-all" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="service_type" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[11px] uppercase tracking-[0.25em] font-default text-muted-foreground">
                Service Interest
              </FormLabel>
              <FormControl>
                <Input placeholder="Website Design" {...field} className="bg-muted border-0 border-b border-border px-4 h-12 text-base font-default text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary rounded-lg transition-all" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="business_size" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[11px] uppercase tracking-[0.25em] font-default text-muted-foreground">
                Business Size
              </FormLabel>
              <FormControl>
                <Input placeholder="1-50 employees" {...field} className="bg-muted border-0 border-b border-border px-4 h-12 text-base font-default text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary rounded-lg transition-all" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="special_requests" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[11px] uppercase tracking-[0.25em] font-default text-muted-foreground">
              Project Details
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your Google listing issues or design goals..." {...field} className="bg-muted border-0 border-b border-border px-4 min-h-[160px] text-base font-default text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary rounded-lg transition-all resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="flex items-start gap-3 pt-2">
        <Checkbox id="newsletter_signup" className="mt-1 border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
        <Label htmlFor="newsletter_signup" className="text-sm font-default text-muted-foreground leading-relaxed cursor-pointer">
          Keep me updated with Asahi Search insights on local SEO and digital growth.
        </Label>
      </div>

      {submitError && <div className="p-4 border border-destructive/40 bg-destructive/10 rounded-lg">
          <p className="text-destructive text-sm font-default">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="group w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.2em] text-sm font-default font-semibold transition-all rounded-lg shadow-md">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Sending...
          </> : <>
            Submit Inquiry
            <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
          </>}
      </Button>
    </Form>;
}
export default function GetInTouch() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="3969" id="get-in-touch" className="bg-background text-foreground relative overflow-hidden pt-32 pb-12 px-6 !h-auto md:pt-40 lg:pt-[160px] md:pb-16 md:px-10 lg:px-16">
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-secondary/30 blur-[140px] pointer-events-none" aria-hidden="true" />

      <div className="max-w-[1440px] mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-[11px] uppercase tracking-[0.3em] font-default text-muted-foreground mb-8">— CONTACT  US</p>

            <h2 className="font-default font-bold text-foreground text-5xl md:text-6xl lg:text-7xl xl:text-[80px] leading-[0.95] tracking-tight mb-10">
              START YOUR
              <br />
              GROWTH<span className="text-primary">.</span>
            </h2>

            <p className="font-default text-lg md:text-xl text-muted-foreground leading-relaxed mb-14 max-w-md">
              Ready to dominate local search or refresh your digital presence? Our team is ready to help you scale.
            </p>

            <div className="space-y-0">
              {contactBlocks.map((block, i) => <div data-index={i} key={i}>
                  {i === 0 && <Separator className="bg-border" />}
                  <div className="py-7">
                    <div className="flex items-center gap-3 mb-3">
                      {block.icon === "phone" && <Phone className="size-5 text-primary" strokeWidth={1.5} />}
                      {block.icon === "mail" && <Mail className="size-5 text-primary" strokeWidth={1.5} />}
                      {block.icon === "clock" && <Clock className="size-5 text-primary" strokeWidth={1.5} />}
                      <p className="text-[11px] uppercase tracking-[0.25em] font-default text-muted-foreground">
                        {block.label}
                      </p>
                    </div>
                    {block.interactive ? <Link to={block.href} className="group inline-flex items-center gap-2 font-default text-xl md:text-[20px] text-foreground hover:text-primary transition-colors">
                        <span>{block.detail}</span>
                        <ArrowRight className="size-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-primary" />
                      </Link> : <p className="font-default text-xl md:text-[20px] text-foreground">{block.detail}</p>}
                  </div>
                  <Separator className="bg-border" />
                </div>)}
            </div>

            
          </div>

          <div ref={rightRef} className={`lg:col-span-7 lg:col-start-7 transition-all duration-1000 delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative bg-background backdrop-blur-sm border border-border p-8 md:p-12 lg:p-[60px] rounded-2xl shadow-lg">
              <FormProvider formKey="get_in_touch_form_contact_us" formId="get_in_touch_form_contact_us" sectionName="get-in-touch" defaultValues={{
              name: "",
              email: "",
              phone: "",
              service_type: "",
              business_size: "",
              special_requests: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2,
                maxLength: 100
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              service_type: {
                required: true
              },
              business_size: {
                required: true
              },
              special_requests: {
                required: false
              }
            }} submitText="Submit Inquiry" successMessage="Thank you for reaching out. An Asahi Search strategist will review your request and contact you within one business day." errorMessage="We encountered an error sending your message. Please try again or email us directly.">
                <ConciergeForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}